import { SnackbarInfo, snackbarStackAtom } from "../atoms/snackbar";
import { useSetAtom } from "jotai";
import { useCallback, useEffect, useRef } from "react";
import { ErrorLike } from "@apollo/client";
import { IconName } from "../components/Icon";

type ShowSnackbarProps = Omit<SnackbarInfo, "_removed" | "_id" | "_height" | "_setHeight">;

const SNACKBAR_AUTO_REMOVE_DURATION = 3000;

export const useSnackbar = () => {
    const setSnackbar = useSetAtom(snackbarStackAtom);

    const removeSnackbar = useCallback(
        (id: string) => {
            setSnackbar((old) => {
                for (const item of old) {
                    if (item._id === id) {
                        if (item.onRemove) {
                            item.onRemove();
                        }
                    }
                }
                return old.filter((item) => item._id !== id);
            });
        },
        [setSnackbar],
    );

    const dismissSnackbar = useCallback(
        (id: string) => {
            setSnackbar((old) =>
                old.map((item) => {
                    if (item._id !== id) return item;
                    return { ...item, _removed: true };
                }),
            );
        },
        [setSnackbar],
    );
    const showSnackbar = useCallback(
        (props: ShowSnackbarProps) => {
            const newId = Math.random().toString(36).substring(0, 10);
            setSnackbar((old) => [...old, { ...props, _removed: false, _id: newId, _height: 0 }]);
            const timeout = props.timeout || SNACKBAR_AUTO_REMOVE_DURATION;
            if (timeout > 0) {
                setTimeout(() => {
                    dismissSnackbar(newId);
                }, timeout);
            }
            return newId;
        },
        [dismissSnackbar, setSnackbar],
    );

    const clearSnackbar = useCallback(() => {
        setSnackbar((old) => old.map((item) => ({ ...item, _removed: true })));
    }, [setSnackbar]);
    return { showSnackbar, clearSnackbar, removeSnackbar, dismissSnackbar };
};

/**
 * Watch for error and Show snackbar when error is not null
 * Optional reset function can be passed when the snackbar is dismissed
 * @param error Apollo error object coming from useMutation
 * @param reset Apollo reset function coming from useMutation
 * @param message Optional message to display ( overrides error.message )
 */
export const useApolloErrorSnackbar = (
    error?: ErrorLike | null,
    reset?: () => void,
    message?: string,
) => {
    const { showSnackbar } = useSnackbar();
    // Tracking the snackbar ID in a ref avoids the cascading re-render that
    // `useState` would trigger; the ID is only read by the cleanup branch.
    const sIdRef = useRef<string | null>(null);

    useEffect(() => {
        if (error) {
            sIdRef.current = showSnackbar({
                mode: "error",
                text: message || error.message,
                onRemove: reset,
            });
        }
    }, [error, message, reset, showSnackbar]);
};

/**
 * Watch for boolean error and Show snackbar when error is true
 * @param hasError
 * @param text Text to display
 * @param icon Icon to display
 */
export const useBooleanErrorSnackbar = (hasError: boolean, text: string, icon?: IconName) => {
    const { showSnackbar, dismissSnackbar } = useSnackbar();
    const sIdRef = useRef<string | null>(null);

    useEffect(() => {
        if (hasError) {
            sIdRef.current = showSnackbar({
                mode: "error",
                text: text,
                icon: icon,
            });
        } else if (sIdRef.current) {
            dismissSnackbar(sIdRef.current);
            sIdRef.current = null;
        }
    }, [dismissSnackbar, hasError, icon, showSnackbar, text]);
};
